import { read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from '../utils/url-manager';

/**
 * Maintenance windows, Stage 5 of `Deployment-Resilience-Plan.md`.
 *
 * Every signed-in admin reads the window, for the banner. Only an admin holding
 * `HQ_System::maintenance` may schedule, start or end one; HQ-API enforces that, and this file only
 * decides what to show.
 *
 * Direct to HQ-API rather than through a react-admin data provider, as the change log is: the
 * providers turn a refusal into an empty result (HQ-06), and a window HQ-API refused to start must
 * not look as though it started.
 */

export const MAINTENANCE_PERMISSION: string = 'HQ_System::maintenance';

/** Dispatched on `window` after any change here, so the banner updates without waiting for its poll. */
export const MAINTENANCE_CHANGED: string = 'hq:maintenance-changed';

export type MaintenancePhase = 'off' | 'scheduled' | 'winding_down' | 'active';

export type MaintenanceWindow = {
    phase: MaintenancePhase,
    /** ISO 8601, UTC. */
    startsAt: string | null,
    /** ISO 8601, UTC. What customers are told; the window does not end by itself when it passes. */
    expectedEndAt: string | null,
    message: string,
    /** Started, and past its expected end. */
    overrun: boolean,
};

export type MaintenanceChange = {
    /** ISO 8601, or blank to start the window now. */
    startsAt: string,
    /** ISO 8601, or blank for no expected end. */
    expectedEndAt: string,
    message: string,
};

export type MaintenanceBannerContent = {
    severity: 'info' | 'warning' | 'error',
    text: string,
};

export const NO_WINDOW: MaintenanceWindow = {
    phase: 'off',
    startsAt: null,
    expectedEndAt: null,
    message: '',
    overrun: false,
};

const PHASES: MaintenancePhase[] = ['off', 'scheduled', 'winding_down', 'active'];

/** In the admin's own time zone, with the zone named, because the window is an instant and admins are not all in one place. */
const windowTimeFormat: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
});

/** An ISO time for reading. Blank for none; anything unreadable is shown as it came. */
export const formatWindowTime = (iso: string | null): string => {
    if (iso === null || iso === '') {
        return '';
    }
    const parsed: Date = new Date(iso);

    return Number.isNaN(parsed.getTime()) ? iso : windowTimeFormat.format(parsed);
};

/** What the banner says for a window, or null when there is nothing to say. */
export const bannerFor = (maintenance: MaintenanceWindow): MaintenanceBannerContent | null => {
    const startsAt: string = formatWindowTime(maintenance.startsAt);
    const expectedEndAt: string = formatWindowTime(maintenance.expectedEndAt);

    switch (maintenance.phase) {
        case 'scheduled':
            return {
                severity: 'info',
                text: `Maintenance is scheduled for ${ startsAt }. The customer portal closes then.`,
            };
        case 'winding_down':
            return {
                severity: 'warning',
                text: `Maintenance starts at ${ startsAt }. Scoring, Email Finder and workflow runs are paused until it ends.`,
            };
        case 'active':
            if (maintenance.overrun) {
                return {
                    severity: 'error',
                    text: `Maintenance has run past its expected end of ${ expectedEndAt }. The customer portal stays closed until it is ended.`,
                };
            }

            return {
                severity: 'warning',
                text: expectedEndAt === ''
                    ? 'Maintenance is in progress. The customer portal is closed.'
                    : `Maintenance is in progress. The customer portal is closed and expected back by ${ expectedEndAt }.`,
            };
        default:
            return null;
    }
};

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * A `datetime-local` input's value, which has no zone and means the admin's own clock, as ISO.
 * Blank stays blank. Anything unreadable is passed on as it is, so HQ-API's refusal names it.
 */
export const localInputToIso = (value: string): string => {
    if (value.trim() === '') {
        return '';
    }
    const parsed: Date = new Date(value);

    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

/** The reverse, to fill the form from a window already set. */
export const isoToLocalInput = (iso: string | null): string => {
    if (iso === null || iso === '') {
        return '';
    }
    const parsed: Date = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }

    return `${ parsed.getFullYear() }-${ pad(parsed.getMonth() + 1) }-${ pad(parsed.getDate()) }`
        + `T${ pad(parsed.getHours()) }:${ pad(parsed.getMinutes()) }`;
};

/** HQ-API's `data`, checked field by field so a malformed answer reads as no window rather than breaking the banner. */
const toWindow = (data: unknown): MaintenanceWindow => {
    const raw: Record<string, unknown> = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;
    const phase: unknown = raw.phase;

    return {
        phase: PHASES.includes(phase as MaintenancePhase) ? phase as MaintenancePhase : 'off',
        startsAt: typeof raw.startsAt === 'string' ? raw.startsAt : null,
        expectedEndAt: typeof raw.expectedEndAt === 'string' ? raw.expectedEndAt : null,
        message: typeof raw.message === 'string' ? raw.message : '',
        overrun: raw.overrun === true,
    };
};

const request = async (path: string, init: RequestInit): Promise<MaintenanceWindow> => {
    const token: unknown = read_cookie('auth');
    if (typeof token !== 'string' || token.length === 0) {
        throw new Error('Not signed in');
    }

    const response: Response = await fetch(new Request(UseUrlManagerCleanUrl({ url: `${ hostUrl }${ path }` }), {
        ...init,
        headers: new Headers({ 'Content-Type': 'application/json', 'token': token, 'hq': 'hq' }),
    }));

    let body: { status?: unknown, message?: unknown, data?: unknown } | null = null;
    try {
        body = await response.json();
    } catch {
        body = null;
    }
    if (!response.ok || body?.status !== 'success') {
        // HQ-API's own reason when it gave one, such as "The expected end must be after the start".
        throw new Error(typeof body?.message === 'string' && body.message !== ''
            ? body.message
            : 'The maintenance window could not be reached');
    }

    return toWindow(body.data);
};

const announced = (maintenance: MaintenanceWindow): MaintenanceWindow => {
    window.dispatchEvent(new Event(MAINTENANCE_CHANGED));

    return maintenance;
};

/** The current window. Throws on any failure. */
export const loadMaintenance = (): Promise<MaintenanceWindow> =>
    request('hq/maintenance', { method: 'GET' });

/** Schedule a window, or start one now with a blank `startsAt`. Replaces any window already set. */
export const scheduleMaintenance = async (change: MaintenanceChange): Promise<MaintenanceWindow> =>
    announced(await request('hq/maintenance/schedule', { method: 'POST', body: JSON.stringify(change) }));

/** End the window, whatever phase it is in. */
export const endMaintenance = async (): Promise<MaintenanceWindow> =>
    announced(await request('hq/maintenance/end', { method: 'POST', body: '{}' }));
